import { useState, useMemo, type FormEvent } from 'react';
import { X, Repeat } from 'lucide-react';
import { createEvent } from '../../hooks/useEvents';
import { getUnavailableMembers } from '../../hooks/useUnavailability';
import type { MemberUnavailability, BandMember } from '../../types/database';
import UnavailabilityConflictModal from './UnavailabilityConflictModal';

interface Props {
  bandId: string;
  userId: string;
  unavailability?: MemberUnavailability[];
  members?: BandMember[];
  onClose: () => void;
  onSuccess: () => void;
}

function toDateStr(d: Date): string {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function addWeeksToDate(dateStr: string, weeks: number): Date {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

function oneYearFrom(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setFullYear(d.getFullYear() + 1);
  return toDateStr(d);
}

function formatPreviewDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function AddRehearsalModal({ bandId, userId, unavailability = [], members = [], onClose, onSuccess }: Props) {
  const [title, setTitle] = useState('Rehearsal');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [venueName, setVenueName] = useState('');
  const [notesPrivate, setNotesPrivate] = useState('');
  const [isSeries, setIsSeries] = useState(false);
  const [everyWeeks, setEveryWeeks] = useState(1);
  const [seriesEndDate, setSeriesEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflictModal, setConflictModal] = useState<{ conflicts: MemberUnavailability[]; date: string } | null>(null);
  const [skipConflictCheck, setSkipConflictCheck] = useState(false);

  function handleStartDateChange(value: string) {
    setStartDate(value);
    if (isSeries && (!seriesEndDate || seriesEndDate <= value)) {
      setSeriesEndDate(oneYearFrom(value));
    }
  }

  function handleSeriesToggle(checked: boolean) {
    setIsSeries(checked);
    if (checked && startDate && !seriesEndDate) {
      setSeriesEndDate(oneYearFrom(startDate));
    }
  }

  const seriesDates = useMemo(() => {
    if (!isSeries || !startDate || !seriesEndDate || everyWeeks < 1) return [];
    const limit = new Date(seriesEndDate + 'T23:59:59');
    const dates: string[] = [];
    for (let i = 0; i < 200; i++) {
      const s = addWeeksToDate(startDate, i * everyWeeks);
      if (s > limit) break;
      dates.push(toDateStr(s));
    }
    return dates;
  }, [isSeries, startDate, seriesEndDate, everyWeeks]);

  function buildEventInput(date: string) {
    const startTimeISO = new Date(`${date}T${startTime}`).toISOString();
    const endTimeISO = endTime ? new Date(`${date}T${endTime}`).toISOString() : undefined;

    return {
      band_id: bandId,
      title,
      event_type: 'rehearsal' as const,
      visibility: 'private' as const,
      start_time: startTimeISO,
      end_time: endTimeISO,
      venue_name: venueName || undefined,
      notes_private: notesPrivate || undefined,
      created_by: userId,
    };
  }

  async function doCreate() {
    setLoading(true);
    setError('');

    if (isSeries && seriesDates.length > 0) {
      const events = seriesDates.map((date) => buildEventInput(date));
      const { error } = await createEvent(events);
      if (error) {
        setError(error);
        setLoading(false);
      } else {
        onSuccess();
      }
    } else {
      const { error } = await createEvent(buildEventInput(startDate));
      if (error) {
        setError(error);
        setLoading(false);
      } else {
        onSuccess();
      }
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!skipConflictCheck && startDate && unavailability.length > 0) {
      // For series, check first date for conflicts as a heads-up
      const dateToCheck = startDate;
      const conflicts = getUnavailableMembers(unavailability, dateToCheck);
      if (conflicts.length > 0) {
        setConflictModal({ conflicts, date: dateToCheck });
        return;
      }
    }

    doCreate();
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
            <h2 className="text-lg font-semibold">Add Rehearsal</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            <p className="text-sm text-gray-400">
              Rehearsals are private and only visible to band members. Use the recurring series option to schedule regular sessions.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Weekly Rehearsal"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Start Time *</label>
                <input
                  type="time"
                  required
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
                <input
                  type="text"
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  placeholder="e.g. Dave's garage"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Notes (band only)</label>
              <textarea
                value={notesPrivate}
                onChange={(e) => setNotesPrivate(e.target.value)}
                rows={2}
                placeholder="e.g. Working on new songs, bring lyrics..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>

            {/* Recurring series toggle */}
            <div className="border border-gray-800 rounded-lg p-3">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSeries}
                  onChange={(e) => handleSeriesToggle(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                />
                <Repeat className={`w-4 h-4 ${isSeries ? 'text-emerald-400' : 'text-gray-500'}`} />
                <span className={`text-sm font-medium ${isSeries ? 'text-gray-50' : 'text-gray-400'}`}>
                  Recurring series
                </span>
              </label>

              {isSeries && (
                <div className="mt-3 space-y-3 pl-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">Every</span>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={everyWeeks}
                      onChange={(e) => setEveryWeeks(Math.max(1, Math.min(52, parseInt(e.target.value) || 1)))}
                      className="w-16 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-50 text-center text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-400">week{everyWeeks !== 1 ? 's' : ''}</span>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Series ends</label>
                    <input
                      type="date"
                      value={seriesEndDate}
                      min={startDate}
                      onChange={(e) => setSeriesEndDate(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Defaults to 1 year from the start date</p>
                  </div>

                  {/* Preview of generated dates */}
                  {startDate && seriesDates.length > 0 && (
                    <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-2.5 max-h-[140px] overflow-y-auto">
                      <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">
                        Preview ({seriesDates.length} rehearsal{seriesDates.length !== 1 ? 's' : ''})
                      </div>
                      <div className="space-y-1">
                        {seriesDates.map((d, i) => (
                          <div key={i} className="text-xs text-gray-300 flex items-center gap-2">
                            <span className="text-gray-500 w-5 text-right">{i + 1}.</span>
                            <span>{formatPreviewDate(d)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-lg px-3 py-2">
              <p className="text-xs text-emerald-400">
                Rehearsals are always private - only your band members can see them.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
              >
                {loading
                  ? 'Adding...'
                  : isSeries && seriesDates.length > 0
                    ? `Add ${seriesDates.length} Rehearsal${seriesDates.length !== 1 ? 's' : ''}`
                    : 'Add Rehearsal'
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      {conflictModal && (
        <UnavailabilityConflictModal
          conflicts={conflictModal.conflicts}
          members={members}
          eventDate={conflictModal.date}
          onCancel={() => setConflictModal(null)}
          onProceed={() => {
            setConflictModal(null);
            setSkipConflictCheck(true);
            doCreate();
          }}
        />
      )}
    </>
  );
}
